/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Pie } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { Typography } from "@mui/material";

// Argon Dashboard 2 PRO MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// PieChart configurations
import configs from "./configs/index";
import "chartjs-plugin-datalabels"; //for datalabels on slice of pie chart
import "./styles.css";

// Default colors for pie chart slices
const defaultColors = [
  "#6C7AF2", // Blue
  "#B6C6E6", // Light Blue
  "#1B2A41", // Dark Blue
  "#6ED6F5", // Cyan
  "#FF6B6B", // Red
  "#4ECDC4", // Teal
  "#45B7D1", // Sky Blue
  "#96CEB4", // Green
];

function PieChart({ title, description, height, chart, showLegend = true }) {
  const { data, options } = configs(chart.labels || [], chart.datasets || {});

  // Generate legend data from chart data
  const legendData = useMemo(() => {
    if (!showLegend || !data.labels || !data.datasets[0]?.data) return [];

    return data.labels.map((label, index) => ({
      name: label,
      percent: data.datasets[0].data[index],
      color:
        data.datasets[0].backgroundColor[index] ||
        defaultColors[index % defaultColors.length],
    }));
  }, [data, showLegend]);

  const PieChartLegend = () => (
    <Grid container spacing={2} mt={1} justifyContent="center">
      {legendData.map((item, index) => (
        <Grid
          item
          key={item.name}
          xs={6}
          sm={6}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingRight: 32,
            paddingLeft: index % 2 === 1 ? 32 : 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: item.color,
                marginRight: 8,
              }}
            />
            <Typography fontSize="16px" fontWeight="600" color="#172B4D">
              {item.name}
            </Typography>
          </div>
          <Typography
            color="rgb(168,184,216)"
            fontSize="14px"
            fontWeight="400"
            style={{
              minWidth: 40,
              textAlign: "right",
              paddingLeft: index % 2 === 1 ? 32 : 0,
            }}
          >
            {item.percent}%
          </Typography>
        </Grid>
      ))}
    </Grid>
  );

  const renderChart = (
    <ArgonBox p={2}>
      {title || description ? (
        <ArgonBox px={description ? 1 : 0} pt={description ? 1 : 0}>
          {title && (
            <ArgonBox mb={1}>
              <ArgonTypography variant="h6">{title}</ArgonTypography>
            </ArgonBox>
          )}
          <ArgonBox mb={2}>
            <ArgonTypography
              component="div"
              variant="button"
              fontWeight="regular"
              color="text"
            >
              {description}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      ) : null}
      {useMemo(
        () => (
          <ArgonBox height={height}>
            <Pie data={data} options={options} />
          </ArgonBox>
        ),
        [chart, height]
      )}
      {showLegend && legendData.length > 0 && <PieChartLegend />}
    </ArgonBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of PieChart
PieChart.defaultProps = {
  title: "",
  description: "",
  height: "19.125rem",
  showLegend: true,
};

// Typechecking props for the PieChart
PieChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.array, PropTypes.object])
  ).isRequired,
  showLegend: PropTypes.bool,
};

export default PieChart;
